import React from 'react';
import { Helmet } from 'react-helmet-async';
import { NavLink, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ShieldCheck, 
  FileText, 
  CreditCard, 
  Activity, 
  Cookie, 
  AlertTriangle,
  Info,
  Calendar,
  Layers
} from 'lucide-react';
import '../styles/privacy-policy.css';

const POLICY_SECTIONS = [
  {
    id: 'terms-and-conditions',
    title: 'Terms of Use',
    icon: <FileText size={20} strokeWidth={3} />,
    version: '2.0',
    effectiveDate: '2026-03-03',
    lastUpdated: '2026-04-20',
    intro:
      'These Terms of Use ("Terms") govern your access to and use of TeachGenie™.ai and related services (collectively, the "Service"), operated by TeachGenie™.ai, YOM Private Limited ("YOM Private Limited," "TeachGenie™.ai," "we," "us," "our"). By clicking "I Agree" (or similar) or by accessing/using the Service, you agree to be bound by these Terms. If you do not agree, do not use the Service.',
    sections: [
      {
        heading: '1. Definitions',
        content: [
          '"Account" means a registered user account.',
          '"Input" means any text, files, prompts, questions, or other materials you submit to the Service.',
          '"Output" means any text, suggestions, drafts, explanations, summaries, or other content generated by the Service in response to Inputs.',
          '"User Content" means Inputs and any other content you upload, submit, or transmit through the Service.',
          '"Subscription" means a paid plan that provides access to paid features.'
        ]
      },
      {
        heading: '2. Service Nature (Service, not a Product)',
        content: [
          'TeachGenie™.ai provides an online service that generates educational and workflow content. The Service is provided on an "as available" basis and may change over time. You are not purchasing a software product; you are receiving access to a service.'
        ]
      },
      {
        heading: '3. Eligibility and Age Gate',
        content: [
          'The Service is intended for users who are at least 18 years old (or the age of majority in your jurisdiction). By using the Service, you represent that you meet this requirement and have capacity to enter into these Terms.'
        ]
      },
      {
        heading: '4. Account Registration and Security',
        content: [
          'You must provide accurate information and keep it updated.',
          'You are responsible for maintaining confidentiality of login credentials and for all activity under your Account.',
          'Notify us promptly of unauthorized access or suspected security incidents at info@TeachGenie.ai.'
        ]
      },
      {
        heading: '5. AI Outputs: Limitations, Verification, and No Reliance',
        content: [
          'The Service uses large language model (LLM) agents to generate Outputs. Outputs are probabilistic and may be inaccurate, incomplete, or outdated. You must independently verify Outputs before relying on them, especially for consequential uses.',
          'TeachGenie™.ai does not warrant that Outputs are correct, complete, or suitable for your purpose. You use Outputs at your own risk and discretion. TeachGenie™.ai is not responsible if the Service fails to generate an Output, generates partial Outputs, or generates incorrect Outputs.'
        ]
      },
      {
        heading: '6. Academic Enrichment and Integrity',
        content: [
          'TeachGenie™.ai is intended for academic enrichment and learning support (e.g., concept clarification, brainstorming, outlining, practice). You are responsible for complying with your institution\'s academic integrity policies.',
          'Do not use the Service to cheat on exams or graded assessments where AI assistance is prohibited.',
          'Do not submit Outputs as solely your own work where disclosure is required.',
          'Use time saved by TeachGenie™.ai to focus on deeper learning, creativity, and applying knowledge (mission statement; not a guarantee).'
        ]
      },
      {
        heading: '7. User Content & Uploads Policy',
        content: [
          {
            type: 'subheading',
            title: '7.1 Your Responsibilities for Uploads',
            text: 'You represent and warrant that you have all rights and permissions required to submit User Content, including any uploaded files. You are solely responsible for your User Content and any consequences of submitting it.'
          },
          {
            type: 'subheading',
            title: '7.2 Prohibited User Content',
            text: 'You must not upload, submit, or request generation of content that:',
            items: [
              'infringes copyright, trademark, privacy, or other rights;',
              'contains confidential or personal information of others without lawful basis/permission;',
              'includes exam papers, answer keys, or materials intended to facilitate cheating where prohibited;',
              'is unlawful, abusive, harassing, hateful, or encourages wrongdoing;',
              'contains malware or attempts to compromise security.'
            ]
          },
          {
            type: 'subheading',
            title: '7.3 License to Operate the Service',
            text: 'You grant TeachGenie™.ai a limited, worldwide, non-exclusive license to host, store, process, reproduce, and transmit your User Content solely to operate, maintain, and improve the Service, including generating Outputs. This license ends when your User Content is deleted from active systems, subject to standard backup/log retention and legal obligations.'
          },
          {
            type: 'subheading',
            title: '7.4 Removal and Enforcement',
            text: 'We may remove or restrict User Content, or suspend/terminate Accounts, where we reasonably believe these Terms are violated or where required by law or safety concerns.'
          }
        ]
      },
      {
        heading: '8. Intellectual Property',
        content: [
          'TeachGenie™.ai retains all rights, title, and interest in the Service, including software, models, prompts, design, and trademarks, except for User Content.',
          'As between you and TeachGenie™.ai, you retain your rights in your Inputs. Subject to these Terms, you may use Outputs for your lawful personal or internal educational purposes. TeachGenie™.ai does not guarantee Outputs are original or non-infringing.'
        ]
      },
      {
        heading: '9. Copyright and Third-Party Rights; Indemnity',
        content: [
          'If copyright or other third-party rights issues arise from your Inputs, your use of Outputs, or content you submit or produce using the Service, you are responsible.',
          'To the fullest extent permitted by law, you agree to indemnify and hold harmless TeachGenie™.ai and its affiliates, directors, officers, employees, and contractors from any claims, damages, losses, and expenses (including reasonable legal fees) arising out of (a) your User Content; (b) your use of Outputs; (c) your violation of these Terms; or (d) your violation of any law or third-party rights.'
        ]
      },
      {
        heading: '10. Subscriptions and Payments',
        content: [
          'Paid features require an active Subscription. Subscription pricing, billing cadence, taxes, cancellation and refund rules are described in the "Subscription, Billing, Cancellation & Refund Policy" and/or at checkout. If there is a conflict between that policy and these Terms on payment matters, the payment policy will control. All subscription fees are non-refundable except as required by applicable law; see Billing/Refund Policy.'
        ]
      },
      {
        heading: '11. Acceptable Use (High-Level)',
        content: [
          'You must not misuse the Service. In addition to Section 7.2, you agree not to:',
          {
            type: 'list',
            items: [
              'reverse engineer, attempt to extract source code, system prompts, or underlying models (except to the extent prohibited by law);',
              'scrape, crawl, or use automated means to access the Service except as expressly permitted;',
              'interfere with the Service, circumvent security, or probe vulnerabilities;',
              'use the Service to generate malware, deceptive content intended to defraud, or content that violates applicable laws.'
            ]
          }
        ]
      },
      {
        heading: '12. Disclaimers',
        content: [
          'THE SERVICE AND OUTPUTS ARE PROVIDED "AS IS" AND "AS AVAILABLE". TO THE MAXIMUM EXTENT PERMITTED BY LAW, TEACHGENIE.AI DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.'
        ]
      },
      {
        heading: '13. Limitation of Liability',
        content: [
          'TO THE MAXIMUM EXTENT PERMITTED BY LAW, TEACHGENIE.AI WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, GOODWILL, OR BUSINESS INTERRUPTION.',
          'TEACHGENIE.AI\'S TOTAL LIABILITY FOR ALL CLAIMS RELATING TO THE SERVICE WILL NOT EXCEED THE GREATER OF (A) THE AMOUNTS YOU PAID TO TEACHGENIE.AI FOR THE SERVICE IN THE THREE (3) MONTHS BEFORE THE EVENT GIVING RISE TO THE CLAIM, OR (B) INR 1,000 IF YOU PAID NOTHING.'
        ]
      },
      {
        heading: '14. Suspension and Termination',
        content: [
          'We may suspend or terminate your access if we reasonably believe you violated these Terms or if your use creates risk or harm. You may stop using the Service at any time. Certain provisions (including IP, disclaimers, limitation of liability, indemnity, and dispute resolution) survive termination.'
        ]
      },
      {
        heading: '15. Changes to the Service or Terms',
        content: [
          'We may modify the Service or these Terms from time to time. We will update the "Last Updated" date. Continued use after changes means you accept the updated Terms.'
        ]
      },
      {
        heading: '16. Governing Law and Dispute Resolution',
        content: [
          'These Terms are governed by the laws of India. Courts located in New Delhi, India will have exclusive jurisdiction, subject to any mandatory arbitration clause you adopt for paid plans. Following steps to be followed in case of dispute:',
          {
            type: 'list',
            items: [
              'Step 1: Mediation (good-faith mediation within 30 days of notice)',
              'Step 2: Arbitration (seat: New Delhi, language: English, arbitrator appointment mechanism)',
              'Step 3: Courts (New Delhi courts for interim relief + enforcement/appeals where applicable)'
            ]
          }
        ]
      },
      {
        heading: '17. Contact',
        content: [
          'Legal and support contact: info@teachgenie.ai'
        ]
      }
    ]
  },
  {
    id: 'privacy-policy',
    title: 'Privacy Policy',
    icon: <ShieldCheck size={20} strokeWidth={3} />,
    effectiveDate: '2026-03-03',
    lastUpdated: '2026-04-20',
    intro:
      'This Privacy Policy explains how TeachGenie™.ai ("TeachGenie™.ai," "we"), YOM Private Limited, collects, uses, shares, and protects personal data when you use TeachGenie™.ai (the "Service"). This Policy is intended to align with India\'s Digital Personal Data Protection Act, 2023 and the Digital Personal Data Protection Rules, 2025, as they come into effect on a phased basis. Your rights and our obligations may evolve as the framework is fully operationalized. Dispute resolution and limitation of liability are governed by the Terms of Use.',
    sections: [
      {
        heading: '1. What We Collect',
        content: [
          'Account data: name, email, login credentials (hashed), organization (if provided).',
          'Usage data: prompts metadata, feature usage, timestamps, device/browser information, IP address, logs.',
          'User Content: files and text you upload or submit as Inputs, and the corresponding Outputs.',
          'Payment data: billing details and transaction identifiers processed via our payment processor (we typically do not store full card details).',
          'Support communications: messages you send to support, and related diagnostic information.'
        ]
      },
      {
        heading: '2. How We Use Data',
        content: [
          'Provide and operate the Service, including generating Outputs from Inputs.',
          'Maintain security, prevent abuse, debug, and improve reliability.',
          'Process payments, manage subscriptions, and provide customer support.',
          'Comply with legal obligations and enforce our Terms and policies.'
        ]
      },
      {
        heading: '3. Legal Basis / Grounds for Processing',
        content: [
          'Where required, we process personal data on the basis of (a) your consent; (b) performance of a contract (providing the Service); (c) legitimate uses permitted by applicable law; and (d) compliance with legal obligations. Where consent is used, you may withdraw consent subject to lawful limitations and the need to provide the Service.'
        ]
      },
      {
        heading: '4. Sharing and Subprocessors',
        content: [
          'We may share data with service providers ("subprocessors") who help us operate the Service, such as hosting providers, AI infrastructure providers, analytics providers, email/communications tools, customer support tools, and payment processors. We require them to protect data and use it only for the services they provide to us.'
        ]
      },
      {
        heading: '5. Cross-Border Transfers',
        content: [
          'Depending on our infrastructure and vendors, data may be processed or stored outside India. Where cross-border transfers occur, we implement contractual and technical safeguards intended to protect data, subject to applicable law and any government restrictions notified from time to time.'
        ]
      },
      {
        heading: '6. Retention and Deletion',
        content: [
          'We retain personal data only as long as necessary for the purposes described in this Policy, including providing the Service, complying with legal obligations, resolving disputes, and enforcing agreements. Deletion requests may not remove residual copies from backups immediately; backups are overwritten on standard retention cycles.'
        ]
      },
      {
        heading: '7. Security',
        content: [
          'We implement commercially reasonable administrative, technical, and organizational safeguards intended to protect personal data against unauthorized access, alteration, disclosure, or destruction. No system can be guaranteed 100% secure.'
        ]
      },
      {
        heading: '8. Your Choices and Rights',
        content: [
          'Access and correction: request access to and correction of your account information.',
          'Deletion: request deletion of your account and associated personal data, subject to lawful exceptions.',
          'Consent withdrawal: where processing is based on consent, you may withdraw consent (which may affect Service functionality).',
          'Grievance: contact us using the details below.'
        ]
      },
      {
        heading: '9. No Minors',
        content: [
          'The Service is intended for users 18+ and is not directed to children. If you believe a minor has provided personal data, contact us to request deletion.'
        ]
      },
      {
        heading: '10. Changes',
        content: [
          'We may update this Policy from time to time. We will update the "Last Updated" date and may provide additional notice for material changes.'
        ]
      },
      {
        heading: '11. Contact / Grievance',
        content: [
          'Privacy and grievance contact: info@teachgenie.ai'
        ]
      }
    ]
  },
  {
    id: 'subscription-billing-refund',
    title: 'Billing & Refund Policy',
    icon: <CreditCard size={20} strokeWidth={3} />,
    effectiveDate: '2026-03-03',
    lastUpdated: '2026-04-20',
    intro:
      'This Policy applies to paid subscriptions to the TeachGenie™.ai Service ("Subscriptions"). It is incorporated by reference into the TeachGenie™.ai Terms of Use. Capitalized terms not defined here have the meaning in the Terms of Use.',
    sections: [
      {
        heading: '1. Plans and Pricing',
        content: [
          'Subscriptions are offered in tiers (for example, "Basic" at INR 299 per month), as described on the pricing page or at checkout. We may add, remove, or modify plans and features over time. Prices may change with notice as described below.'
        ]
      },
      {
        heading: '2. Billing and Auto-Renewal',
        content: [
          'Subscriptions are billed in advance on a recurring basis (monthly/annual as selected).',
          'Unless you cancel before renewal, your Subscription will auto-renew and you authorize us (and our payment processor) to charge the payment method on file.',
          'You are responsible for applicable taxes (GST) and payment processor fees, if any, shown at checkout.'
        ]
      },
      {
        heading: '3. Trials and Promotions',
        content: [
          'If a free trial or promotional pricing is offered, the trial duration and terms will be shown at signup/checkout. Unless canceled before the trial ends, your paid Subscription will begin automatically at the then-current price.'
        ]
      },
      {
        heading: '4. Cancellation',
        content: [
          'You may cancel at any time from your account settings (or by contacting support). Cancellation stops future renewals. Unless required by law or explicitly stated at checkout, cancellation does not entitle you to a refund for the current billing period.'
        ]
      },
      {
        heading: '5. Refunds',
        content: [
          'Refund eligibility depends on the plan and the reason for the request and may be limited by payment processor rules and applicable law. Unless otherwise stated at checkout:',
          {
            type: 'list',
            items: [
              'Fees are non-refundable except where required by law, once a billing period begins.',
              'We may issue discretionary refunds or credits in cases of duplicate charges, unauthorized charges (after investigation), or material service failure.',
              'Service credits under an SLA (if applicable) are the primary remedy for availability shortfalls for SLA-covered plans.',
              'Disputes are to be resolved as per Section 16 of the Terms of Use.'
            ]
          }
        ]
      },
      {
        heading: '6. Payment Failures',
        content: [
          'If payment fails or is reversed/charged back, we may suspend or downgrade your access until payment is received. Repeated chargebacks may result in termination.'
        ]
      },
      {
        heading: '7. Changes to Pricing or This Policy',
        content: [
          'We may change prices or this Policy by providing notice (e.g., in-app notice or email). New prices apply at the next renewal unless we state otherwise or unless required by law.'
        ]
      },
      {
        heading: '8. Contact',
        content: [
          'Billing support: info@teachgenie.ai'
        ]
      }
    ]
  },
  {
    id: 'sla-addendum',
    title: 'SLA Addendum',
    icon: <Activity size={20} strokeWidth={3} />,
    effectiveDate: '2026-03-03',
    lastUpdated: '2026-03-03',
    intro:
      'This SLA Addendum applies only to paid Subscription plans explicitly designated as "SLA-covered" at checkout or on the pricing page. It sets forth availability and support commitments and the exclusive remedies (service credits) for failure to meet those commitments.',
    sections: [
      {
        heading: '1. Definitions',
        content: [
          '"Monthly Uptime Percentage" means total minutes in a calendar month minus Downtime, divided by total minutes in the month, expressed as a percentage.',
          '"Downtime" means the Service is unavailable to the user for reasons within TeachGenie™\'s control, excluding Exclusions below.',
          '"Service Credit" means a credit against future subscription fees, not a refund, calculated as described below.'
        ]
      },
      {
        heading: '2. Availability Commitment',
        content: ['We target Monthly Uptime Percentage of 99.5% for SLA-covered plans.']
      },
      {
        heading: '3. Exclusions',
        content: [
          'Downtime does not include unavailability caused by:',
          {
            type: 'list',
            items: [
              'planned maintenance with reasonable notice (including emergency maintenance);',
              'customer equipment, software, or internet/ISP issues;',
              'third-party outages outside our reasonable control (e.g., upstream cloud provider-wide incidents);',
              'force majeure events;',
              'suspension due to Terms violations or abuse, including denial-of-service or security incidents.'
            ]
          }
        ]
      },
      {
        heading: '4. Support Hours and Response Targets',
        content: [
          'Support is provided via email at info@teachgenie.ai. Support hours and response targets depend on plan tier and are displayed at checkout. Example targets:',
          {
            type: 'list',
            items: [
              'Basic: initial response within 2 business days.',
              'Premium: initial response within 1 business day.'
            ]
          }
        ]
      },
      {
        heading: '5. Service Credits for Availability Shortfall',
        content: [
          'If Monthly Uptime Percentage falls below 99.5% in a given month for an SLA-covered plan, eligible customers may claim the following Service Credits (against future fees):',
          {
            type: 'table',
            headers: ['Monthly Uptime %', 'Credit % of Monthly Fee', 'Notes'],
            rows: [
              ['99.0% to <99.5%', '5%', ''],
              ['98.0% to <99.0%', '10%', ''],
              ['<98.0%', '25%', '']
            ]
          }
        ]
      },
      {
        heading: '6. Credit Claim Process',
        content: [
          'To request credits, email info@teachgenie.ai within 30 days after the month ends, including account email, the affected month, and a brief description.',
          'Credits will be applied to the next invoice cycle if approved.',
          'Credits are the sole and exclusive remedy for failure to meet SLA targets.'
        ]
      },
      {
        heading: '7. Credit Cap and Non-Stacking',
        content: [
          'Total Service Credits for a month are capped at 25% of the monthly fee for the affected SLA-covered plan and do not stack with other discretionary credits unless we agree otherwise in writing.'
        ]
      },
      {
        heading: '8. Relationship to Terms',
        content: [
          'This SLA is part of the contract between you and TeachGenie™.ai. In case of conflict, this SLA controls only with respect to availability/support credits; otherwise the Terms of Use control.'
        ]
      }
    ]
  },
  {
    id: 'cookie-policy',
    title: 'Cookie Policy',
    icon: <Cookie size={20} strokeWidth={3} />,
    effectiveDate: '2026-03-03',
    lastUpdated: '2026-03-03',
    intro:
      'This Cookie Policy explains how TeachGenie™.ai uses cookies and similar technologies ("Cookies") on TeachGenie™.ai. Cookies are small text files stored on your device that help websites function and improve user experience.',
    sections: [
      {
        heading: '1. Types of Cookies We Use',
        content: [
          'Strictly necessary cookies: required for core site functionality, security, and account login.',
          'Preferences cookies: remember settings (e.g., language).',
          'Analytics cookies (if enabled): help us understand usage and improve the Service.',
          'Marketing cookies (if enabled): used to measure campaigns or deliver relevant ads.'
        ]
      },
      {
        heading: '2. Cookie List',
        content: [
          'We maintain a list of Cookies we use, including name, provider, purpose, and duration.',
          {
            type: 'table',
            headers: ['Cookie/Tool', 'Purpose', 'Type', 'Duration'],
            rows: [
              ['session_id', 'Login/session security', 'Necessary', 'Session'],
              ['GA Client ID', 'Analytics (Google Analytics)', 'Analytics', 'Up to 2 years'],
              ['Meta Pixel', 'Marketing measurement', 'Marketing', 'Up to 90 days']
            ]
          }
        ]
      },
      {
        heading: '3. Your Choices',
        content: [
          'You can control Cookies through your browser settings and, where available, through our cookie banner/preferences center. If you disable necessary Cookies, parts of the Service may not function.'
        ]
      },
      {
        heading: '4. Updates',
        content: [
          'We may update this Cookie Policy from time to time as our tooling changes. We will update the "Last Updated" date.'
        ]
      },
      {
        heading: '5. Contact',
        content: ['Cookie questions: info@teachgenie.ai']
      }
    ]
  },
  {
    id: 'disclaimers',
    title: 'Disclaimers',
    icon: <AlertTriangle size={20} strokeWidth={3} />,
    effectiveDate: '2026-03-03',
    lastUpdated: '2026-03-03',
    intro:
      'TeachGenie™.ai provides AI-generated educational content for general learning and academic enrichment. Outputs are generated by large language model (LLM) systems and may be inaccurate, incomplete, outdated, or otherwise imperfect.',
    sections: [
      {
        heading: 'Full Legal Disclaimer',
        content: [
          'Do not rely on Outputs as a substitute for professional advice or your own independent research. You are responsible for verifying accuracy and ensuring your use complies with academic policies and applicable laws, including copyright. TeachGenie™.ai may not generate results in some cases and may generate incorrect results.',
          'TeachGenie™.ai does not guarantee Outputs are original or non-infringing. If copyright or third-party issues arise from your Inputs or use of Outputs, you are responsible as described in the Terms of Use.'
        ]
      }
    ]
  }
];

const findActivePolicy = (policyId) => {
  const fallback = POLICY_SECTIONS[0];
  if (!policyId) return fallback;
  return POLICY_SECTIONS.find((item) => item.id === policyId) || fallback;
};

const ContentBlock = ({ block }) => {
  if (typeof block === 'string') {
    return <p className="policies-text-block">{block}</p>;
  }

  if (block.type === 'list') {
    return (
      <ul className="policies-points">
        {block.items.map((item, idx) => (
          <li key={idx}>{item}</li>
        ))}
      </ul>
    );
  }

  if (block.type === 'subheading') {
    return (
      <div className="policies-sub-section">
        <h4>{block.title}</h4>
        <p>{block.text}</p>
        {block.items && (
          <ul className="policies-points">
            {block.items.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  if (block.type === 'table') {
    return (
      <div className="policies-table-wrapper">
        <table className="policies-table">
          <thead>
            <tr>
              {block.headers.map((h, idx) => (
                <th key={idx}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, rowIdx) => (
              <tr key={rowIdx}>
                {row.map((cell, cellIdx) => (
                  <td key={cellIdx}>{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return null;
};

const PolicyArticle = ({ policy }) => (
  <motion.article 
    key={policy.id}
    initial={{ opacity: 0, x: 20 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: -20 }}
    transition={{ duration: 0.3, ease: 'easeOut' }}
    className="policies-content" 
    aria-live="polite"
  >
    <header className="policies-hero">
      <div className="policies-hero-header">
        <picture>
          <source srcSet="/TechGenieMascot.webp" type="image/webp" />
          <img src="/TechGenieMascot.png" alt="TeachGenie™" className="policies-hero-mascot" />
        </picture>
        <div>
          <p className="policies-eyebrow">TeachGenie™ Legal Center</p>
          <p style={{ fontSize: '11px', color: '#94a3b8', margin: '2px 0 4px', fontWeight: 600, letterSpacing: '0.5px', textTransform: 'uppercase' }}>- YOM Private Limited</p>
          <h2>{policy.title}</h2>
        </div>
      </div>
      
      <div className="policies-badges">
        {policy.version && (
          <div className="policies-badge version">
            <Layers size={14} /> Version {policy.version}
          </div>
        )}
        {policy.effectiveDate && (
          <div className="policies-badge date">
            <Calendar size={14} /> Effective: {policy.effectiveDate}
          </div>
        )}
        <div className="policies-badge update">
          <Info size={14} /> Updated: {policy.lastUpdated}
        </div>
      </div>

      <p className="policies-intro">{policy.intro}</p>
    </header>

    <div className="policies-main-body">
      {policy.sections.map((section, sIdx) => (
        <motion.section 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 + sIdx * 0.05 }}
          className="policies-section" 
          key={section.heading}
        >
          <div className="policies-section-header">
            <h3>{section.heading}</h3>
          </div>
          <div className="policies-body">
            {section.content.map((block, idx) => (
              <ContentBlock key={idx} block={block} />
            ))}
          </div>
        </motion.section>
      ))}
    </div>
  </motion.article>
);

const SidebarLink = ({ section, isExpanded }) => (
  <NavLink
    to={`/policies/${section.id}`}
    className={({ isActive }) => (isActive ? 'is-active' : '')}
  >
    <span className="nav-icon">{section.icon}</span>
    <AnimatePresence>
      {isExpanded && (
        <motion.span 
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -10 }}
          className="nav-label"
        >
          {section.title}
        </motion.span>
      )}
    </AnimatePresence>
  </NavLink>
);

// Matches the `@media (max-width: 768px)` breakpoint in privacy-policy.css
// where the sidebar is hidden. Below this width the content wrapper must
// not receive animated inline margin/padding (see isMobile usage below) —
// inline styles set by Framer Motion's `animate` prop always win over
// stylesheet rules, so without this check the desktop sidebar-offset stays
// pinned in place even inside the mobile media query, crushing the content
// column into a sliver on small screens.
const useIsMobile = (breakpoint = 768) => {
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== 'undefined' && window.innerWidth <= breakpoint
  );
  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const handler = (e) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, [breakpoint]);
  return isMobile;
};

export default function PrivacyPolicy() {
  const { policyId } = useParams();
  const activePolicy = findActivePolicy(policyId);
  const [isExpanded, setIsExpanded] = React.useState(false);
  const isMobile = useIsMobile();

  return (
    <>
      <Helmet>
        <title>{`${activePolicy.title} | TeachGenie™ Policies`}</title>
        <meta
          name="description"
          content={`${activePolicy.title} on TeachGenie™ Policies. Review legal, privacy, billing, cookie, SLA, and disclaimer terms in a structured format.`}
        />
      </Helmet>

      <main className="policies-page">
        <div className="policies-layout">
          <motion.aside 
            className={`policies-sidebar ${isExpanded ? 'is-expanded' : ''}`} 
            aria-label="Policy navigation"
            onMouseEnter={() => setIsExpanded(true)}
            onMouseLeave={() => setIsExpanded(false)}
            animate={{ width: isExpanded ? 260 : 70 }}
            transition={{ type: 'spring', stiffness: 500, damping: 40 }}
          >
            <div className="sidebar-inner">
              <div className="sidebar-header">
                <picture>
                  <source srcSet="/TechGenieMascot.webp" type="image/webp" />
                  <img src="/TechGenieMascot.png" alt="TeachGenie™" className="sidebar-mascot" />
                </picture>
                <AnimatePresence>
                  {isExpanded && (
                    <motion.h1
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      Legal Center
                    </motion.h1>
                  )}
                </AnimatePresence>
              </div>
              
              <nav className="sidebar-nav">
                {POLICY_SECTIONS.map((section) => (
                  <SidebarLink key={section.id} section={section} isExpanded={isExpanded} />
                ))}
              </nav>
              
              <div className="sidebar-footer">
                <AnimatePresence mode="wait">
                  {isExpanded ? (
                    <motion.div 
                      key="expanded-footer"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="sidebar-footer-content"
                    >
                      <p>Need help?</p>
                      <a href="mailto:info@teachgenie.ai">info@teachgenie.ai</a>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="collapsed-footer"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="collapsed-help-icon"
                    >
                      <Info size={20} color="#e2e8f0" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </motion.aside>


          
          <motion.div
            className="policies-content-wrapper"
            // On mobile, animate={} entirely — even paddingLeft/marginLeft
            // set to 0 would still be inline styles that force just the
            // LEFT side to exactly 0, leaving the CSS shorthand (padding:
            // 40px 24px) in control of right/top/bottom only. That gave a
            // lopsided flush-left layout instead of even margins on both
            // sides. Omitting the keys entirely lets CSS control all sides.
            animate={isMobile ? {} : { paddingLeft: isExpanded ? 40 : 80, marginLeft: isExpanded ? 280 : 90 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          >
            <AnimatePresence mode="wait">
              <PolicyArticle key={activePolicy.id} policy={activePolicy} />
            </AnimatePresence>
          </motion.div>
        </div>
      </main>


    </>
  );
}


